import {
  FormErrorMessage,
  Image,
  Input,
  useDisclosure,
} from "@chakra-ui/react";
import { Controller } from "react-hook-form";
import { IMCFormInvoiceDetail } from "./interface";
import CardFormAlt from "../CardFormAlt";
import ResponsiveFormControl from "../ResponsiveFormControl";
import { ModalContent } from "../FullWidthModal";
import { InheritModalContentProps } from "../FullWidthModal/interface";
import moment from "moment";
import CustomReactSelect from "../CustomReactSelect";
import { getPaymentTypeName } from "../../helpers/global";
import { PaymentType } from "../../generated/graphql";
import FieldSearchModal from "../FieldSearchModal";
import ModalSearchPaymentDetail from "../ModalSearchPaymentDetail";
import ModalSearchSignature from "../ModalSearchSignature";
import ModalSearchSignatureBackground from "../ModalSearchSignatureBackground";

const MCFormInvoiceDetail: React.FC<
  InheritModalContentProps<IMCFormInvoiceDetail>
> = ({ ...props }) => {
  const {
    onOpen: onOpenPaymentDetail,
    onClose: onClosePaymentDetail,
    isOpen: isOpenPaymentDetail,
  } = useDisclosure();

  const {
    onOpen: onOpenSignature,
    onClose: onCloseSignature,
    isOpen: isOpenSignature,
  } = useDisclosure();

  const {
    onOpen: onOpenSignatureBackground,
    onClose: onCloseSignatureBackground,
    isOpen: isOpenSignatureBackground,
  } = useDisclosure();

  return (
    <ModalContent
      {...props}
      defaultValues={{
        invoiceDate: moment().format("Y-MM-DD"),
      }}
    >
      {({ control, register, errors, watch, setValue }) => {
        const watchPaymentDetail = watch("paymentDetail");
        const watchSignature = watch("signature");
        const watchSignatureBackground = watch("signatureBackground");

        return (
          <form autoComplete="off">
            <CardFormAlt
              boxShadow="rgba(0, 0, 0, 0.04) 0px 1px 4px"
              borderRadius="8px"
              padding="26px 24px"
              title="Invoice Information"
              mb={10}
            >
              <ResponsiveFormControl
                label="Invoice Date"
                isInvalid={errors.hasOwnProperty("invoiceDate")}
                isRequired
                messageComponents={
                  <FormErrorMessage>Invoice Date is required!</FormErrorMessage>
                }
              >
                <Input
                  w="50%"
                  type="date"
                  {...register("invoiceDate", { required: true })}
                />
              </ResponsiveFormControl>
              <ResponsiveFormControl
                label="Payment Type"
                isRequired
                isInvalid={errors.hasOwnProperty("paymentType")}
                messageComponents={
                  <FormErrorMessage>Payment Type is required!</FormErrorMessage>
                }
              >
                <Controller
                  name="paymentType"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => {
                    const options = [
                      {
                        label: getPaymentTypeName(PaymentType.Cash),
                        value: PaymentType.Cash,
                      },
                      {
                        label: getPaymentTypeName(PaymentType.Credit),
                        value: PaymentType.Credit,
                      },
                    ];

                    return (
                      <CustomReactSelect
                        placeholder="Select Payment Type"
                        size="sm"
                        value={options.find((elm) => elm.value === value)}
                        options={options}
                        onChange={(res) => {
                          onChange(res?.value);
                        }}
                        isSearchable={false}
                        isClearable
                      />
                    );
                  }}
                />
              </ResponsiveFormControl>
              <ResponsiveFormControl
                label="Ref. No"
                isRequired
                isInvalid={errors.hasOwnProperty("refNo")}
                messageComponents={
                  <FormErrorMessage>Ref. No is required!</FormErrorMessage>
                }
              >
                <Input {...register("refNo", { required: true })} />
              </ResponsiveFormControl>
            </CardFormAlt>

            <CardFormAlt title="Customer Information" mb={10}>
              <ResponsiveFormControl
                label="Customer Name"
                isRequired
                isInvalid={errors.hasOwnProperty("customerName")}
                messageComponents={
                  <FormErrorMessage>
                    Customer Name is required!
                  </FormErrorMessage>
                }
              >
                <Input {...register("customerName", { required: true })} />
              </ResponsiveFormControl>

              <ResponsiveFormControl
                label="Customer Signature Name"
                isRequired
                isInvalid={errors.hasOwnProperty("customerSignatureName")}
                messageComponents={
                  <FormErrorMessage>
                    Customer Signature Name is required!
                  </FormErrorMessage>
                }
              >
                <Input
                  {...register("customerSignatureName", { required: true })}
                />
              </ResponsiveFormControl>

              <ResponsiveFormControl
                label="Booker Name"
                isRequired
                isInvalid={errors.hasOwnProperty("bookerName")}
                messageComponents={
                  <FormErrorMessage>Booker Name is required!</FormErrorMessage>
                }
              >
                <Input {...register("bookerName", { required: true })} />
              </ResponsiveFormControl>
            </CardFormAlt>

            <CardFormAlt title="Signature Information" mb={10}>
              <ResponsiveFormControl label="Signature">
                <FieldSearchModal
                  value={watchSignature?.name || ""}
                  onSearchClick={() => onOpenSignature()}
                  onClearClick={() => setValue("signature", null)}
                  placeholder="Use Default"
                  showClearButton={watchSignature ? true : false}
                />
                <Input type="hidden" {...register("signature")} />
              </ResponsiveFormControl>
              {watchSignature && (
                <ResponsiveFormControl label="Image">
                  <Image src={watchSignature.imageUrl} h="80px" w="auto" />
                </ResponsiveFormControl>
              )}

              <ResponsiveFormControl label="Signature Background">
                <FieldSearchModal
                  value={watchSignatureBackground?.name || ""}
                  onSearchClick={() => onOpenSignatureBackground()}
                  onClearClick={() => setValue("signatureBackground", null)}
                  placeholder="Use Default"
                  showClearButton={watchSignatureBackground ? true : false}
                />
                <Input type="hidden" {...register("signatureBackground")} />
              </ResponsiveFormControl>
              {watchSignatureBackground && (
                <ResponsiveFormControl label="Image">
                  <Image
                    src={watchSignatureBackground.imageUrl}
                    h="80px"
                    w="auto"
                  />
                </ResponsiveFormControl>
              )}
            </CardFormAlt>
            <ModalSearchSignature
              isOpen={isOpenSignature}
              onClose={onCloseSignature}
              onSelect={(item) => {
                setValue("signature", item, {
                  shouldValidate: true,
                });
                onCloseSignature();
              }}
            />
            <ModalSearchSignatureBackground
              isOpen={isOpenSignatureBackground}
              onClose={onCloseSignatureBackground}
              onSelect={(item) => {
                setValue("signatureBackground", item, {
                  shouldValidate: true,
                });
                onCloseSignatureBackground();
              }}
            />
            <CardFormAlt title="Payment Detail Information">
              <ResponsiveFormControl label="Company Name">
                <FieldSearchModal
                  value={watchPaymentDetail?.companyName || ""}
                  onSearchClick={() => onOpenPaymentDetail()}
                  onClearClick={() => setValue("paymentDetail", null)}
                  placeholder="Use Default"
                  showClearButton={watchPaymentDetail ? true : false}
                />
                <Input type="hidden" {...register("paymentDetail")} />
              </ResponsiveFormControl>
              {watchPaymentDetail && (
                <>
                  <ResponsiveFormControl label="Branch Name">
                    <Input
                      disabled
                      value={watchPaymentDetail?.branchName || ""}
                    />
                  </ResponsiveFormControl>
                  <ResponsiveFormControl label="Bank No">
                    <Input disabled value={watchPaymentDetail?.bankNo || ""} />
                  </ResponsiveFormControl>
                  <ResponsiveFormControl label="Swift Code">
                    <Input
                      disabled
                      value={watchPaymentDetail?.swiftCode || ""}
                    />
                  </ResponsiveFormControl>
                  <ResponsiveFormControl label="Account No">
                    <Input
                      disabled
                      value={watchPaymentDetail?.accountNo || ""}
                    />
                  </ResponsiveFormControl>
                </>
              )}
            </CardFormAlt>

            <ModalSearchPaymentDetail
              isOpen={isOpenPaymentDetail}
              onClose={onClosePaymentDetail}
              onSelect={(item) => {
                setValue("paymentDetail", item, {
                  shouldValidate: true,
                });
                onClosePaymentDetail();
              }}
            />
          </form>
        );
      }}
    </ModalContent>
  );
};

export default MCFormInvoiceDetail;
